import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database } from 'lucide-react';
import { databasesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import wmLogo from '../assets/logos/WM-Logo-CB-White-Header.png';

const DatabasePickerPage = () => {
  const navigate = useNavigate();
  const { accessDenied, loading: authLoading } = useAuth();
  const [databases, setDatabases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && accessDenied) navigate('/access-denied', { replace: true });
  }, [authLoading, accessDenied, navigate]);

  useEffect(() => {
    databasesApi.getAvailable()
      .then(res => setDatabases(res.data || []))
      .catch(() => setDatabases([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSelect = (db) => {
    const slug = (db.displayName || db.name).toLowerCase();
    localStorage.setItem('pmd-selected-db-slug', slug);
    navigate(`/${slug}/`);
  };

  useEffect(() => {
    if (!loading && databases.length === 1) handleSelect(databases[0]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, databases]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: '#1a5568', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="auth-gate-card">

        {/* Branded header — same as auth gate step card */}
        <div className="auth-gate-header">
          <img src={wmLogo} alt="Wunderlich-Malec" className="auth-gate-logo" />
          <div className="auth-gate-header-divider" />
          <div>
            <div className="auth-gate-title">DAWN</div>
            <div className="auth-gate-subtitle">DOCUMENTATION · ASSETS · WIRING · NETWORKS</div>
          </div>
        </div>

        {/* Title bar */}
        <div style={{
          padding: '1rem 1.75rem 0.875rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          gap: '0.625rem',
          flexShrink: 0
        }}>
          <Database size={20} style={{ color: '#1a5568', flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: '1rem', color: '#1e293b', lineHeight: 1.2 }}>Select a Database</div>
            <div style={{ fontSize: '0.775rem', color: '#64748b', marginTop: 2 }}>Choose a project database to get started.</div>
          </div>
        </div>

        {/* Scrollable database list */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0.625rem 1rem', minHeight: 0 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b', fontSize: '0.875rem' }}>Loading...</div>
          ) : databases.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#64748b', fontSize: '0.875rem' }}>
              No databases available for your account.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {databases.map(db => {
                const name = db.displayName || db.name;
                return (
                  <button
                    key={db.id || name}
                    onClick={() => handleSelect(db)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.875rem',
                      padding: '0.625rem 1rem',
                      background: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: 8,
                      cursor: 'pointer',
                      textAlign: 'left',
                      width: '100%',
                      transition: 'border-color 0.15s, box-shadow 0.15s'
                    }}
                    onMouseOver={e => {
                      e.currentTarget.style.borderColor = '#1a5568';
                      e.currentTarget.style.boxShadow = '0 0 0 1px #1a5568';
                    }}
                    onMouseOut={e => {
                      e.currentTarget.style.borderColor = '#e2e8f0';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    {db.iconUrl && (
                      <img src={db.iconUrl} alt="" style={{ width: 28, height: 28, borderRadius: 4, objectFit: 'contain', flexShrink: 0 }} />
                    )}
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1e293b' }}>{name}</div>
                      {db.description && (
                        <div style={{ fontSize: '0.775rem', color: '#64748b', marginTop: 1 }}>{db.description}</div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default DatabasePickerPage;
